/**
 * Servicio dual de emails: Mandrill + AWS SES
 * Maneja el envío de emails a través de múltiples proveedores
 */
import { contactEmailTemplate } from '../templates/email/contactEmail';
import { confirmationEmailTemplate } from '../templates/email/confirmationEmail';
import { SESClient, SendEmailCommand } from '@aws-sdk/client-ses';

/**
 * Envía email principal via Mandrill
 * @param {Object} data - Datos del formulario (nombre, email, motivo, mensaje)
 * @param {string} recaptchaToken - Token de reCAPTCHA para validación
 * @returns {Promise<Array>} Respuesta de Mandrill API
 * 
 * Comportamiento:
 * - REACT_APP_SEND_EMAILS="0": Retorna respuesta mock (testing)
 * - REACT_APP_SEND_EMAILS="1": Envía email real via Mandrill
 * 
 * Destinatarios:
 * - TO: info@amalgamatech.com (Amalgamatech recibe consulta)
 */
export const sendEmailMandrill = async (data, recaptchaToken) => {
  // Modo testing: retorna respuesta simulada sin enviar email real
  if (process.env.REACT_APP_SEND_EMAILS === '0') {
    console.log('🚫 Email sending disabled - returning mock response');
    return [{ status: 'sent', provider: 'mandrill-mock', _id: 'mock-' + Date.now() }];
  }
  
  const apiKey = process.env.REACT_APP_MANDRILL_API_KEY;
  
  try {
    const response = await fetch('https://mandrillapp.com/api/1.0/messages/send.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        key: apiKey,
        message: {
          ...contactEmailTemplate(data),
          from_email: process.env.REACT_EMAIL_FROM_ADDRESS_TO_AMALGAMA || 'info@amalgamatech.com',
          from_name: 'Amalgamatech Contact Form',
          to: [{
            email: process.env.REACT_EMAIL_FROM_ADDRESS_TO_AMALGAMA || 'info@amalgamatech.com',
            name: process.env.REACT_EMAIL_FROM_NAME || 'Amalgamatech',
            type: 'to'
          }],
          reply_to: data.email
        }
      })
    });
    
    if (!response.ok) {
      throw new Error(`Mandrill API error: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Mandrill error:', error);
    throw error;
  }
};

/**
 * Envía email via AWS SES
 * @param {Object} data - Datos del formulario
 * @param {string} recaptchaToken - Token de reCAPTCHA
 * @returns {Promise<Object>} Respuesta de AWS SES
 */
export const sendEmailAWS = async (data, recaptchaToken) => {
  if (process.env.REACT_APP_SEND_EMAILS === '0') {
    console.log('🚫 Email sending disabled - returning mock response');
    return { status: 'sent', provider: 'aws-mock', MessageId: 'mock-' + Date.now() };
  }
  
  if (!process.env.REACT_APP_AWS_ACCESS_KEY_ID || !process.env.REACT_APP_AWS_SECRET_ACCESS_KEY) {
    throw new Error('AWS credentials not configured');
  }
  
  try {
    const sesClient = new SESClient({
      region: process.env.REACT_APP_AWS_REGION || 'eu-west-1',
      credentials: {
        accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
        secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY
      }
    });
    
    const params = {
      Source: process.env.REACT_EMAIL_FROM_ADDRESS_TO_AMALGAMA || 'info@amalgamatech.com',
      Destination: {
        ToAddresses: [process.env.REACT_EMAIL_FROM_ADDRESS_TO_AMALGAMA || 'info@amalgamatech.com']
      },
      Message: {
        Subject: {
          Data: `${data.motivo}`,
          Charset: 'UTF-8'
        },
        Body: {
          Html: {
            Data: contactEmailTemplate(data).html,
            Charset: 'UTF-8'
          },
          Text: {
            Data: contactEmailTemplate(data).text,
            Charset: 'UTF-8'
          }
        }
      },
      ReplyToAddresses: [data.email]
    };
    
    const command = new SendEmailCommand(params);
    return await sesClient.send(command);
    
  } catch (error) {
    console.error('AWS SES error:', error);
    throw error;
  }
};

/**
 * Envía email de confirmación al usuario via Mandrill
 * @param {Object} data - Datos del formulario
 * @returns {Promise<Array>} Respuesta de Mandrill API
 * 
 * NOTA: Actualmente no se usa porque el email principal
 * ya incluye CC al usuario. Se mantiene para compatibilidad.
 * 
 * Template: Email de agradecimiento personalizado
 * Destinatario: data.email (solo al usuario)
 */
export const sendConfirmationMandrill = async (data) => {
  // Modo testing: retorna respuesta simulada
  if (process.env.REACT_APP_SEND_EMAILS === '0') {
    console.log('🚫 Email sending disabled - returning mock confirmation');
    return [{ status: 'sent', provider: 'mandrill-confirmation-mock', _id: 'mock-confirm-' + Date.now() }];
  }
  
  const apiKey = process.env.REACT_APP_MANDRILL_API_KEY;
  
  try {
    const response = await fetch('https://mandrillapp.com/api/1.0/messages/send.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        key: apiKey,
        message: {
          ...confirmationEmailTemplate(data),
          from_email: process.env.REACT_EMAIL_FROM_ADDRESS_TO_SENDER || 'no-reply@amalgamatech.com',
          from_name: process.env.REACT_EMAIL_FROM_NAME || 'Amalgamatech',
          to: [{
            email: data.email,
            name: data.nombre,
            type: 'to'
          }]
        }
      })
    });
    
    if (!response.ok) {
      throw new Error(`Mandrill API error: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Mandrill confirmation error:', error);
    throw error;
  }
};

export const sendConfirmationAWS = async (data) => {
  if (process.env.REACT_APP_SEND_EMAILS === '0') {
    console.log('🚫 Email sending disabled - returning mock confirmation');
    return { status: 'sent', provider: 'aws-confirmation-mock', MessageId: 'mock-confirm-' + Date.now() };
  }
  
  if (!process.env.REACT_APP_AWS_ACCESS_KEY_ID || !process.env.REACT_APP_AWS_SECRET_ACCESS_KEY) {
    throw new Error('AWS credentials not configured');
  }
  
  try {
    const sesClient = new SESClient({
      region: process.env.REACT_APP_AWS_REGION || 'eu-west-1',
      credentials: {
        accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
        secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY
      }
    });
    
    const params = {
      Source: process.env.REACT_EMAIL_FROM_ADDRESS_TO_SENDER || 'no-reply@amalgamatech.com',
      Destination: {
        ToAddresses: [data.email]
      },
      Message: {
        Subject: {
          Data: confirmationEmailTemplate(data).subject,
          Charset: 'UTF-8'
        },
        Body: {
          Html: {
            Data: confirmationEmailTemplate(data).html,
            Charset: 'UTF-8'
          },
          Text: {
            Data: confirmationEmailTemplate(data).text,
            Charset: 'UTF-8'
          }
        }
      }
    };
    
    const command = new SendEmailCommand(params);
    return await sesClient.send(command);
    
  } catch (error) {
    console.error('AWS SES confirmation error:', error);
    throw error;
  }
};